import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Activity,
  RefreshCw,
  Loader2,
  LogIn,
  Calendar,
  FileText,
  Users,
  Eye,
  UserMinus,
  MapPin,
  Trash2,
  Filter,
  Webhook,
  X
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL } from '@/lib/apiBase';

const API = API_BASE_URL;

const ActivityPage = () => {
  const { isAdmin } = useAuth();
  const [activities, setActivities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [actionFilter, setActionFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      // Get unique users from activities
      const response = await axios.get(`${API}/activities?limit=500`);
      const uniqueUsers = [];
      const seenUserIds = new Set();
      
      response.data.forEach(activity => {
        if (activity.user_id && !seenUserIds.has(activity.user_id)) {
          seenUserIds.add(activity.user_id);
          uniqueUsers.push({
            id: activity.user_id,
            name: activity.user_name || 'Unknown User'
          });
        }
      });
      
      setUsers(uniqueUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      
      if (actionFilter !== 'all') {
        params.append('action', actionFilter);
      }
      if (locationFilter !== 'all') {
        params.append('location_id', locationFilter);
      }
      if (userFilter !== 'all') {
        params.append('user_id', userFilter);
      }
      
      const response = await axios.get(`${API}/activities?${params.toString()}`);
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  }, [actionFilter, locationFilter, userFilter]);

  useEffect(() => {
    fetchLocations();
    fetchUsers();
  }, [fetchLocations, fetchUsers]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleClearActivities = async () => {
    setClearing(true);
    try {
      await axios.delete(`${API}/activities`);
      toast.success('Activities cleared');
      setActivities([]);
      setClearDialogOpen(false);
    } catch (error) {
      toast.error('Failed to clear activities');
    } finally {
      setClearing(false);
    }
  };

  const clearFilters = () => {
    setActionFilter('all');
    setLocationFilter('all');
    setUserFilter('all');
  };

  const hasFilters = actionFilter !== 'all' || locationFilter !== 'all' || userFilter !== 'all';

  const getActivityIcon = (action) => {
    if (action.includes('webhook')) return <Webhook className="w-4 h-4" />;
    switch (action) {
      case 'login':
        return <LogIn className="w-4 h-4" />;
      case 'logout':
        return <LogIn className="w-4 h-4 rotate-180" />;
      case 'field_sync':
      case 'sync_all':
        return <FileText className="w-4 h-4" />;
      case 'calendar_sync':
        return <Calendar className="w-4 h-4" />;
      case 'user_sync':
      case 'user_removed':
        return <Users className="w-4 h-4" />;
      case 'field_update':
      case 'field_visibility':
        return <Eye className="w-4 h-4" />;
      case 'calendar_update':
        return <Calendar className="w-4 h-4" />;
      case 'offboarding':
        return <UserMinus className="w-4 h-4" />;
      case 'location_add':
      case 'location_update':
      case 'location_delete':
        return <MapPin className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getActivityColor = (action) => {
    if (action.includes('webhook')) return 'bg-violet-100 text-violet-700';
    if (action.includes('login')) return 'bg-green-100 text-green-700';
    if (action.includes('sync')) return 'bg-blue-100 text-blue-700';
    if (action.includes('field')) return 'bg-purple-100 text-purple-700';
    if (action.includes('calendar')) return 'bg-amber-100 text-amber-700';
    if (action.includes('offboarding') || action.includes('removed')) return 'bg-rose-100 text-rose-700';
    if (action.includes('location')) return 'bg-indigo-100 text-indigo-700';
    return 'bg-slate-100 text-slate-700';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} days ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAction = (action) => {
    return action.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading && activities.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="activity-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Activity Log
          </h1>
          <p className="text-slate-500 mt-1">
            Track all sync operations, field updates, and user actions
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchActivities}
            disabled={loading}
            data-testid="refresh-activities-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh
          </Button>
          {isAdmin() && (
            <Button 
              variant="outline"
              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
              onClick={() => setClearDialogOpen(true)}
              data-testid="clear-activities-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-500">Filters:</span>
        </div>
        
        {/* Action Filter */}
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-[180px]" data-testid="action-filter">
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="login">Logins</SelectItem>
            <SelectItem value="sync_all">Sync All</SelectItem>
            <SelectItem value="field_sync">Field Syncs</SelectItem>
            <SelectItem value="calendar_sync">Calendar Syncs</SelectItem>
            <SelectItem value="user_sync">User Syncs</SelectItem>
            <SelectItem value="field_update">Field Updates</SelectItem>
            <SelectItem value="field_visibility">Visibility Changes</SelectItem>
            <SelectItem value="user_removed">User Removed</SelectItem>
            <SelectItem value="offboarding">Offboarding</SelectItem>
            <SelectItem value="webhook_contact">Webhook: Contact</SelectItem>
            <SelectItem value="webhook_field">Webhook: Field</SelectItem>
          </SelectContent>
        </Select>

        {/* Location Filter */}
        <Select value={locationFilter} onValueChange={setLocationFilter}>
          <SelectTrigger className="w-[200px]" data-testid="location-filter">
            <SelectValue placeholder="All Locations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {locations.map((loc) => (
              <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* User Filter */}
        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger className="w-[180px]" data-testid="user-filter">
            <SelectValue placeholder="All Users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            {users.map((usr) => (
              <SelectItem key={usr.id} value={usr.id}>{usr.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Clear Filters */}
        {hasFilters && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={clearFilters}
            className="text-slate-500"
          >
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}

        <span className="text-sm text-slate-500 ml-auto">
          {activities.length} activities
        </span>
      </div>

      {/* Activities List */}
      {activities.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="py-12 text-center">
            <Activity className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {hasFilters ? 'No activities match your filters' : 'No activities yet'}
            </h3>
            <p className="text-slate-500">
              {hasFilters ? 'Try adjusting your filters' : 'Activities will appear here as you use the platform'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {activities.map((activity) => (
            <Card key={activity.id} className="overflow-hidden" data-testid={`activity-${activity.id}`}>
              <div className="p-3 flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.action)}`}>
                  {getActivityIcon(activity.action)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`text-xs ${getActivityColor(activity.action)}`}>
                      {formatAction(activity.action)}
                    </Badge>
                    {activity.location_name && (
                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                        {activity.location_name}
                      </span>
                    )}
                    <span className="text-xs text-slate-400">{formatTimestamp(activity.timestamp)}</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-0.5 truncate">{activity.details}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Clear Confirmation */}
      <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Activities</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to clear all activity logs? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleClearActivities}
              disabled={clearing}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-clear-btn"
            >
              {clearing ? 'Clearing...' : 'Clear All'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ActivityPage;
